import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ToysIcon from '@mui/icons-material/Toys';
import Chip from '@mui/material/Chip';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/CachedOutlined';
import DoneIcon from '@mui/icons-material/DoneOutlined';
import CancelledIcon from '@mui/icons-material/HighlightOff';
import {
  getGridStringOperators,
  GridCellParams,
  GridColumns,
  GridColumnVisibilityModel,
  GridFilterInputValue,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridPinnedColumns,
  GridRenderCellParams,
  GridRowId,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import { format, parse } from 'date-fns';
import { withStyles } from '@mui/styles';
import { DocumentFile, RecievedStatus } from '../generated/graphql';
import { DATE_FORMAT } from '../lib/date_time';
import { RECEIVEDSTATUS, TABLE_NAMES } from '../lib/constants';
import { EditableCell } from './datagrid/EditableCell';
import { TimePickerEditCell } from './datagrid/TimePickerEditCell';
import { getLoadingList } from '../components/DownloadLoadingList';
import { TerminalTimeInfo } from './datagrid/TerminalTimeInfoModal';
import { LoadingListModal } from './datagrid/LoadingListModal';
import { QRModal } from './datagrid/QRModal';
import { SelectDate } from './SelectDate';
import { DownloadCMR } from './datagrid/DownloadCMRCell';
import { TftSelectDate } from './TftSelectDate';
import EditDriverDetailsModal from './Traffic/routes/route/EditDriverDetailsModal';
import EditNorwayColumnsModal from './Traffic/routes/route/EditNorwayColumnsModal';
import { resolveLoadTypeFromLeg } from '../utils/checkpointUtils';
import {
  TERMINAL_STATUS_FALLBACK_OPTION,
  TERMINAL_STATUS_OPTIONS_WITH_ICONS,
} from '../utils/terminalStatusOptions';

interface TftReadOnlyConfigType {
  columnOrder?: string[];
  filterModel?: GridFilterModel;
  sortModel?: GridSortModel;
  pinnedColumns?: GridPinnedColumns;
  gridColumnVisibilityModel?: GridColumnVisibilityModel;
  columnsWidth?: any;
}

interface ListIncomingColumnParams {
  apiRef: any;
  classes: any;
  t: any;
  httpClient: any;
  globalDateFormat: string;
  locationId: number;
  truckFillAndTimeMutation: any;
  tftConfig: TftReadOnlyConfigType;
  visibilityRef: React.MutableRefObject<GridColumnVisibilityModel>;
  setErrorInfo: (value: {
    errorInField: boolean;
    errorMessage: string;
  }) => void;
  getChipStatus: (row: any) => string;
  handleUploadClick: (data: {
    routeName: string;
    legId: number;
    subcontractorName: string;
    existingFiles: DocumentFile[];
  }) => void;
  handleCellChecked: (id: any, isChecked: boolean, field: string) => void;
  handleSealBrokenChecked: (id: any, isChecked: boolean) => void;
  updateActualArrivalDate: (
    id: GridRowId,
    legId: number,
    date: string,
    field: string,
    actualArrivalTime?: string,
  ) => void;
  handleCarArrived: (params: GridRenderCellParams) => void;
  handleCarDeparted: (params: GridRenderCellParams) => void;
  getBackColor: (percentage: number) => string;
  canEditCell: (leg: any, field: string) => boolean;
  setActiveTerminalStatusLegId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  onNorwayColumnsUpdate?: (updatedFields: {
    id: string | number;
    [key: string]: any;
  }) => void;
  onDriverDetailsUpdate?: (updatedFields: {
    id: string | number;
    driverName: string;
    driverPhoneNumber: string;
    carRegistrationNumber: string;
    trailerRegistrationNumber: string;
  }) => void;
  noTextWrap?: boolean;
}

const filterOperatorsCountry: GridFilterOperator[] = [
  ...getGridStringOperators(),
  {
    label: 'is not',
    value: 'isNot',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.columnField ||
        !filterItem.value ||
        !filterItem.operatorValue
      ) {
        return null;
      }

      return (params): boolean => {
        return params.value !== filterItem.value;
      };
    },
    InputComponent: GridFilterInputValue,
  },
];

const RegisteredChip = withStyles({
  root: {
    color: '#12de63',
    borderColor: '#12de63',
  },
})(Chip);

const KippRegisteredChip = withStyles({
  root: {
    color: '#ffa600',
    borderColor: '#ffa600',
  },
})(Chip);

const UnregisteredChip = withStyles({
  root: {
    color: '#f07267',
    borderColor: '#f07267',
  },
})(Chip);

const CancelledChip = withStyles({
  root: {
    color: '#fc3926',
    borderColor: '#fc3926',
  },
})(Chip);

const PartialRegisteredChip = withStyles({
  root: {
    color: '#eda528',
    borderColor: '#eda528',
  },
})(Chip);

export function listIncomingColumns({
  apiRef,
  classes,
  t,
  httpClient,
  globalDateFormat,
  locationId,
  truckFillAndTimeMutation,
  tftConfig,
  visibilityRef,
  setErrorInfo,
  getChipStatus,
  handleUploadClick,
  handleCellChecked,
  handleSealBrokenChecked,
  updateActualArrivalDate,
  handleCarArrived,
  handleCarDeparted,
  getBackColor,
  canEditCell,
  setActiveTerminalStatusLegId,
  onNorwayColumnsUpdate,
  onDriverDetailsUpdate,
  noTextWrap,
}: ListIncomingColumnParams): GridColumns {
  const makeVisibilityAwareQuickFilter = (field: string) => {
    return (value: any) => {
      if (visibilityRef.current[field] === false) {
        return null;
      }
      if (!value) return null;
      const filterRegex = new RegExp(
        value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        'i',
      );
      return (params: GridCellParams) => {
        return params.value != null
          ? filterRegex.test(params.value.toString())
          : false;
      };
    };
  };

  const colArr: GridColumns = [
    {
      field: 'id',
      headerName: 'id',
      disableColumnMenu: true,
      type: 'string',
    },
    {
      field: 'viewLoadingList',
      headerName: 'View Loading List',
      minWidth: 100,
      type: 'actions',
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const { legId, routeId2, routeId } = row;
        return (
          <LoadingListModal
            legId={legId}
            routeId={routeId2}
            routeName={routeId}
            httpClient={httpClient}
            loadingListError={() =>
              setErrorInfo({
                errorInField: true,
                errorMessage: t('validation.loadingListError'),
              })
            }
          />
        );
      },
    },

    {
      field: 'downloadLoadingList',
      headerName: 'Download Loading List',
      minWidth: 180,
      type: 'actions',
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <Tooltip
            title={`${t('actions.viewItem', {
              item: t('resource.loadingList.capitalized'),
            })}`}
          >
            <IconButton
              onClick={() => {
                getLoadingList(
                  parseInt(row.routeId2, 10),
                  row.routeId,
                  httpClient,
                );
              }}
            >
              <CloudDownloadIcon color="action" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'DownloadCMR',
      headerName: 'Download CMR',
      minWidth: 100,
      type: 'actions',
      sortable: false,

      renderCell: (params: GridCellParams) => {
        const { legId, routeId } = params.row;
        return (
          <DownloadCMR
            legId={legId}
            routeId={routeId}
            downloadCMRError={() => {
              setErrorInfo({
                errorInField: true,
                errorMessage: t('validation.noCMRFound'),
              });
            }}
          />
        );
      },
    },
    {
      headerName: 'Terminal Nickname',
      field: 'terminalNickname',
      minWidth: 100,
      editable: true,
      type: 'string',

      renderCell(params) {
        return (
          <EditableCell
            apiRef={apiRef}
            editable={canEditCell(params.row, params.field)}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 140,
      sortable: false,
      type: 'string',
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const chipText = getChipStatus(row);
        if (chipText === t('attributes.kippRegistered')) {
          return (
            <KippRegisteredChip
              label={chipText}
              variant="outlined"
              key={row.id}
              icon={<DoneIcon />}
            />
          );
        }
        if (chipText === t('attributes.registeredStatus')) {
          return (
            <RegisteredChip
              label={chipText}
              variant="outlined"
              key={row.id}
              icon={<DoneIcon />}
            />
          );
        } else if (chipText === t('attributes.unregisteredStatus')) {
          return (
            <UnregisteredChip
              label={chipText}
              variant="outlined"
              key={row.id}
              icon={<InfoIcon />}
            />
          );
        } else if (chipText === t('attributes.partialRegisteredStatus')) {
          return (
            <PartialRegisteredChip
              label={chipText}
              variant="outlined"
              key={row.id}
              icon={<RefreshIcon />}
            />
          );
        } else if (chipText === t('attributes.cancelledStatus')) {
          return (
            <CancelledChip
              label={chipText}
              variant="outlined"
              key={row.id}
              icon={<CancelledIcon />}
            />
          );
        } else {
          return null;
        }
      },
      valueGetter: (params: GridValueGetterParams) => getChipStatus(params.row),
    },
    {
      headerName: 'TerminalStatus Icon',
      field: 'terminalStatusIcon',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params: GridCellParams) => {
        const currentStatusOption =
          TERMINAL_STATUS_OPTIONS_WITH_ICONS.find(
            (option) => option.status === params.row.currentTerminalStatus,
          ) ?? TERMINAL_STATUS_FALLBACK_OPTION;
        const StatusIcon = currentStatusOption.icon;

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tooltip title={params.row.currentTerminalStatusDescription || ''}>
              <IconButton
                size="small"
                onClick={() => setActiveTerminalStatusLegId(params.row.legId)}
                sx={{
                  borderRadius: 1,
                  border: `1px solid ${currentStatusOption.color}`,
                  color: currentStatusOption.color,
                  transform: currentStatusOption.rotate
                    ? `rotate(${currentStatusOption.rotate}deg)`
                    : 'none',
                }}
              >
                <StatusIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      headerName: 'Status description',
      field: 'terminalStatusDescription',
      minWidth: 220,
      type: 'string',
      valueGetter: ({ row }) => row.terminalStatusDescription ?? '',
      renderCell: (params: GridCellParams) => {
        return (
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              px: 1,
              whiteSpace: 'normal',
              backgroundColor: params.row.isUnplannedRoute
                ? '#f72c25'
                : 'transparent',
              color: params.row.isUnplannedRoute ? '#fff' : 'inherit',
            }}
          >
            {(params.row.terminalStatusDescription as string) || '-'}
          </Typography>
        );
      },
    },
    {
      headerName: 'Terminal status description',
      field: 'currentTerminalStatusDescription',
      minWidth: 220,
      type: 'string',
      valueGetter: ({ row }) => row.currentTerminalStatusDescription ?? '',
      renderCell: (params: GridCellParams) => {
        const description = (params.value as string) || '';
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
            {description || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'priority',
      headerName: t('gridColumns.priority', 'Priority'),
      width: 100,
      type: 'singleSelect',
      editable: true,
      valueOptions: ['1', '2', '3', '4', '5'],
    },
    {
      field: 'receivedStatus',
      headerName: t('gridColumns.receivedStatus'),
      width: 120,
      type: 'singleSelect',
      editable: true,
      valueOptions: () => Object.values(RECEIVEDSTATUS),
      valueGetter: (params) => {
        const statusMap: Record<string, string> = {
          PickupKipp: RECEIVEDSTATUS.PickupKipp,
          HandedOverKipp: RECEIVEDSTATUS.HandedOverKipp,
          ReceivedAndHandled: RECEIVEDSTATUS.ReceivedAndHandled,
          ReceivedNotStarted: RECEIVEDSTATUS.ReceivedNotStarted,
        };
        return statusMap[params.row.receivedStatus] || null;
      },
      valueParser(value) {
        if (
          value === 'Received and Handled' ||
          value === RECEIVEDSTATUS.ReceivedAndHandled
        ) {
          return 'ReceivedAndHandled' as RecievedStatus;
        } else if (
          value === 'Received not Started' ||
          value === RECEIVEDSTATUS.ReceivedNotStarted
        ) {
          return 'ReceivedNotStarted' as RecievedStatus;
        } else if (value === RECEIVEDSTATUS.HandedOverKipp) {
          return 'HandedOverKipp' as RecievedStatus;
        } else if (value === RECEIVEDSTATUS.PickupKipp) {
          return 'PickupKipp' as RecievedStatus;
        } else {
          return 'None';
        }
      },
    },
    {
      headerName: t('gridColumns.terminalReceivedBy'),
      field: 'receivedBy',
      minWidth: 120,
      type: 'string',
    },
    {
      headerName: t('gridColumns.terminalReceivedTimestamp'),
      field: 'timeWhenReceived',
      minWidth: 50,
      width: 120,
      type: 'string',
    },
    {
      headerName: t('gridColumns.kippReceivedBy'),
      field: 'kippReceivedBy',
      minWidth: 120,
      type: 'string',
    },
    {
      headerName: t('gridColumns.kippReceivedTimestamp'),
      field: 'kippTimeWhenReceived',
      minWidth: 50,
      width: 120,
      type: 'string',
    },
    {
      headerName: 'Non-arrival',
      field: 'nonArrival',
      minWidth: 100,
      type: 'boolean',
      align: 'center',
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const {
          isProductionDayToday,
          isNonArrivalMarked,
          legId,
          transportationDate,
        } = row;
        if (
          isProductionDayToday === true ||
          transportationDate < format(new Date(), DATE_FORMAT)
        ) {
          return (
            <Checkbox
              style={{ padding: 0, color: '#ff0202b8' }}
              checked={isNonArrivalMarked}
              size="small"
              onChange={(_, checked) => {
                handleCellChecked(legId, checked, 'isNonArrivalMarked');
              }}
            />
          );
        } else {
          return <></>;
        }
      },
      valueGetter: (params: GridValueGetterParams) =>
        params.row.isNonArrivalMarked,
    },
    {
      headerName: 'Route ID',
      field: 'routeId',
      minWidth: 100,
      pinnable: true,
      type: 'string',
    },
    {
      headerName: 'Route Type',
      field: 'type',
      minWidth: 100,
      type: 'string',
      valueGetter({ row }) {
        if (row.isCreatedFromTerminal) {
          return 'Terminal';
        }
        return row.type;
      },
    },
    {
      headerName: 'Subcontractor',
      field: 'subcontractorName',
      minWidth: 100,
      align: 'left',
      type: 'string',
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const { subcontractorId } = row;
        if (!subcontractorId || subcontractorId === 0) {
          return null;
        }
        return (
          <Link
            variant="body2"
            href={`/terminal/cards/subcontractors/${subcontractorId}`}
            target={'_blank'}
            underline={'hover'}
            color={'inherit'}
          >
            {row.subcontractorName}
          </Link>
        );
      },
    },
    {
      headerName: 'Driver',
      field: 'driverName',
      minWidth: 120,
      editable: true,
      type: 'string',
      renderCell(params: GridCellParams) {
        return (
          <EditDriverDetailsModal
            routeId={params.row.routeId2}
            legId={params.row.legId}
            routeName={params.row.routeId}
            transportationDate={params.row.transportationDate}
            cellValue={params.value as string}
            onRowUpdate={(updatedFields) =>
              onDriverDetailsUpdate?.({
                ...updatedFields,
                id: params.row.legId,
              })
            }
            tableName={TABLE_NAMES.TruckFillAndTime}
          />
        );
      },
    },
    {
      headerName: 'Capacity',
      field: 'capacity',
      minWidth: 100,
      valueFormatter({ value }) {
        if (value == null) {
          return '';
        }
        return value;
      },
    },
    {
      headerName: 'Actual Car Capacity',
      field: 'actualCarCapacity',
      minWidth: 100,
      editable: true,
      type: 'number',
      renderCell(params) {
        return (
          <EditableCell
            apiRef={apiRef}
            editable={canEditCell(params.row, params.field)}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Actual Trailer Capacity',
      field: 'actualTrailerCapacity',
      minWidth: 100,
      editable: true,
      type: 'number',
      renderCell(params) {
        return (
          <EditableCell
            apiRef={apiRef}
            editable={canEditCell(params.row, params.field)}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Departure Terminal',
      field: 'departureTerminal',
      width: 100,
      type: 'string',
    },
    {
      headerName: 'Departure Location Short Code',
      field: 'departureTerminalLocationCode',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Arriving Terminal',
      field: 'arrivingTerminal',
      width: 100,
      type: 'string',
    },
    {
      headerName: 'First Leg Terminal',
      field: 'firstLegTerminal',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Last Leg Terminal',
      field: 'lastLegTerminal',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Departure City',
      field: 'departureCity',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Arrival City',
      field: 'arrivalCity',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Arrival Location Short Code',
      field: 'arrivingTerminalLocationCode',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Departure Country',
      filterOperators: filterOperatorsCountry,
      field: 'departureCountry',
      minWidth: 100,
      valueFormatter({ value }) {
        if (typeof value !== 'string') {
          return '';
        }
        return value.toUpperCase();
      },
    },
    {
      headerName: 'Arrival Country',
      filterOperators: filterOperatorsCountry,
      field: 'arrivalCountry',
      minWidth: 100,
      type: 'string',
      valueFormatter({ value }) {
        if (typeof value !== 'string') {
          return '';
        }
        return value.toUpperCase();
      },
    },
    {
      headerName: t('attributes.fromToToCountry'),
      field: 'fromToToCountry',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Transportation Date',
      field: 'transportationDate',
      minWidth: 110,
      type: 'date',
      renderCell: (params: GridCellParams) => {
        const { value } = params;
        if (typeof value === 'string') {
          const dateFormat = globalDateFormat.split(' ')[0];
          return format(parse(value, 'yyyy-MM-dd', new Date()), dateFormat);
        }
        return '';
      },
    },
    {
      headerName: 'Arrival time',
      field: 'arrivalTime',
      minWidth: 100,
      valueFormatter(params) {
        const { value } = params;
        if (typeof value === 'string' && value.match(/\d{2}:\d{2}:\d{2}/)) {
          return format(parse(value, 'HH:mm:ss', new Date()), 'HH:mm');
        }
        return '';
      },
    },
    {
      headerName: 'Arrival Status',
      field: 'truckStatusBasedOnTime',
      minWidth: 110,
      cellClassName: (params: GridCellParams) =>
        params.value === 'LATE' ? classes.muiLateStatusCell : '',
    },
    {
      headerName: 'Departure time',
      field: 'departureTime',
      minWidth: 100,
      valueFormatter(params) {
        const { value } = params;
        if (typeof value === 'string' && value.match(/\d{2}:\d{2}:\d{2}/)) {
          return format(parse(value, 'HH:mm:ss', new Date()), 'HH:mm');
        }
        return '';
      },
    },
    {
      headerName: 'Load type',
      field: 'loadType',
      minWidth: 100,
      pinnable: true,
    },
    {
      headerName: 'QR Driver',
      field: 'carCarrierIdQR',
      minWidth: 100,
      type: 'actions',
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { carCarrierId, routeId, routeId2 } = params.row;
        return carCarrierId ? (
          <QRModal id={carCarrierId} routeName={routeId} routeId={routeId2} />
        ) : null;
      },
    },
    {
      field: 'carArrived',
      headerName: 'Car arrived',
      type: 'actions',
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleCarArrived(params)}
        >
          {'Arrived'}
        </Button>
      ),
    },
    {
      headerName: 'AAD',
      field: 'actualArrivalDate',
      minWidth: 140,
      type: 'date',
      editable: true,
      description: 'Actual Arrival Date',
      renderEditCell(params) {
        const { row, id, value, field } = params;
        return (
          <TftSelectDate
            value={value}
            onDateSelected={(selDate: any) => {
              if (selDate) {
                apiRef.current.setEditCellValue({ id, field, value: selDate });
                updateActualArrivalDate(
                  id,
                  row.legId,
                  selDate,
                  'actualDepartureDate',
                );
              }
            }}
          />
        );
      },
      renderCell(params) {
        const { row, id } = params;
        return (
          <SelectDate
            currentDate={row.actualArrivalDate}
            onDateSelected={(selDate: any) => {
              if (selDate)
                updateActualArrivalDate(
                  id,
                  row.legId,
                  selDate,
                  'actualDepartureDate',
                );
            }}
          />
        );
      },
    },
    {
      headerName: 'AAT',
      field: 'actualArrivalTime',
      minWidth: 100,
      type: 'dateTime',
      editable: true,
      description: 'Actual Arrival Time',
      renderEditCell(params) {
        return <TimePickerEditCell {...params} apiRef={apiRef} />;
      },
      renderCell(params) {
        const { value } = params;
        let time = '';
        if (typeof value === 'string') {
          if (value.match(/\d{2}:\d{2}:\d{2}/)) {
            time = format(parse(value, 'HH:mm:ss', new Date()), 'HH:mm');
          } else if (value.match(/\d{2}:\d{2}/)) {
            time = format(parse(value, 'HH:mm', new Date()), 'HH:mm');
          }
        }
        return (
          <EditableCell
            highlightField={true}
            className={classes.editableCell}
            apiRef={apiRef}
            editable={true}
            {...params}
            value={time}
          />
        );
      },
    },
    {
      field: 'carDeparted',
      headerName: 'Car Departed',
      type: 'actions',
      width: 150,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleCarDeparted(params)}
            disabled={!(row.actualArrivalDate && row.actualArrivalTime)}
          >
            {'Departed'}
          </Button>
        );
      },
    },
    {
      headerName: 'ADD ',
      field: 'actualDepartureDate',
      minWidth: 140,
      type: 'date',
      editable: true,
      description: 'Actual Departure Date',
      renderEditCell(params) {
        const { row, id, field, value } = params;

        return (
          <TftSelectDate
            disabled={!(row.actualArrivalDate && row.actualArrivalTime)}
            value={value}
            onDateSelected={(selDate: any) => {
              if (selDate) {
                apiRef.current.setEditCellValue({ id, field, value: selDate });
                truckFillAndTimeMutation({
                  variables: {
                    truckFillAndTimeInput: {
                      id: row.legId,
                      locationId,
                      actualDepartureDate: selDate,
                    },
                  },
                })
                  .then(() => {
                    apiRef.current.setCellMode(id, field, 'view');
                  })
                  .catch((err: Error) => {
                    setErrorInfo({
                      errorInField: true,
                      errorMessage: err.message,
                    });
                  });
              }
            }}
          />
        );
      },
      renderCell(params) {
        const { row } = params;
        return (
          <SelectDate
            currentDate={row.actualDepartureDate}
            onDateSelected={(selDate: any) => {
              if (selDate) {
                truckFillAndTimeMutation({
                  variables: {
                    truckFillAndTimeInput: {
                      id: row.legId,
                      locationId,
                      actualDepartureDate: selDate,
                    },
                  },
                }).catch((err: Error) => {
                  setErrorInfo({
                    errorInField: true,
                    errorMessage: err.message,
                  });
                });
              }
            }}
            disabled={
              row.actualArrivalDate && row.actualArrivalTime ? false : true
            }
          />
        );
      },
    },
    {
      headerName: 'ADT',
      field: 'actualDepartureTime',
      minWidth: 100,
      type: 'dateTime',
      editable: true,
      description: 'Actual Departure Time',
      renderEditCell(params) {
        const { row } = params;
        return (
          <TimePickerEditCell
            {...params}
            apiRef={apiRef}
            disabled={!(row.actualArrivalDate && row.actualArrivalTime)}
          />
        );
      },
      renderCell(params) {
        const { value, row } = params;
        let time = '';
        if (typeof value === 'string') {
          if (value.match(/\d{2}:\d{2}:\d{2}/)) {
            time = format(parse(value, 'HH:mm:ss', new Date()), 'HH:mm');
          } else if (value.match(/\d{2}:\d{2}/)) {
            time = format(parse(value, 'HH:mm', new Date()), 'HH:mm');
          }
        }

        return (
          <EditableCell
            highlightField={true}
            className={classes.editableCell}
            apiRef={apiRef}
            editable={row.actualArrivalDate && row.actualArrivalTime}
            {...params}
            value={time}
          />
        );
      },
    },
    {
      headerName: 'Terminal time info',
      field: 'timeObj',
      editable: true,
      filterable: false,
      type: 'actions',
      renderCell(params) {
        return (
          <TerminalTimeInfo
            timeObj={params.row.timeObj}
            routeId={params.row.routeId}
          />
        );
      },
    },
    {
      headerName: 'Dock date',
      field: 'actualDockDate',
      minWidth: 140,
      type: 'date',
    },
    {
      headerName: 'Dock time',
      field: 'actualDockTime',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Undock date',
      field: 'actualUndockDate',
      minWidth: 140,
      type: 'date',
    },
    {
      headerName: 'Undock time',
      field: 'actualUndockTime',
      minWidth: 100,
      type: 'string',
    },
    {
      headerName: 'Car pallets',
      field: 'carPallets',
      minWidth: 100,
      editable: true,
      type: 'number',

      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            editable={canEditCell(params.row, params.field)}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Trailer pallets',
      field: 'trailerPallets',
      minWidth: 100,
      editable: true,
      type: 'number',

      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            className={classes.editableCell}
            editable={canEditCell(params.row, params.field)}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Pallets factor',
      field: 'palletsFactor',
      minWidth: 170,
      renderCell(params) {
        if (
          resolveLoadTypeFromLeg(params.row) === 'Checkpoint' ||
          (params.row.load && params.row.unload)
        ) {
          return null;
        } else if (params.row.palletsFactor === '') {
          return (
            <Box width={'100%'}>
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #000',
                  color: '#ffffff',
                }}
              >
                <Typography align="left">
                  <label>{'0'}</label>
                </Typography>
              </div>
            </Box>
          );
        } else if (params.row.load) {
          return (
            <Box width={'100%'}>
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #000',
                }}
              >
                <div
                  style={{
                    width: `${
                      params.row.loadLoadFactor > 100
                        ? 100
                        : params.row.loadLoadFactor
                    }%`,
                    backgroundColor: `${getBackColor(
                      params.row.loadLoadFactor,
                    )}`,
                    paddingLeft: 4,
                  }}
                >
                  <Typography align="left">
                    <label>{params.row.loadSumPPI}</label>
                    <label>{' | '}</label>
                    <label>
                      <strong>{params.row.loadLoadFactor}%</strong>
                    </label>
                  </Typography>
                </div>
              </div>
            </Box>
          );
        } else {
          return (
            <Box width={'100%'}>
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #000',
                }}
              >
                <div
                  style={{
                    width: `${
                      params.row.unloadLoadFactor > 100
                        ? 100
                        : params.row.unloadLoadFactor
                    }%`,
                    backgroundColor: `${getBackColor(
                      params.row.unloadLoadFactor,
                    )}`,
                    paddingLeft: 4,
                  }}
                >
                  <Typography align="left">
                    <label>{params.row.unloadSumPPI}</label>
                    <label>{' | '}</label>
                    <label>
                      <strong>{params.row.unloadLoadFactor}%</strong>
                    </label>
                  </Typography>
                </div>
              </div>
            </Box>
          );
        }
      },
    },
    {
      headerName: 'Car PPL',
      field: 'carPPL',
      minWidth: 100,
      editable: true,
      type: 'number',

      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            editable={canEditCell(params.row, params.field)}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Trailer PPL',
      field: 'trailerPPL',
      minWidth: 100,
      editable: true,
      type: 'number',

      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            className={classes.editableCell}
            editable={canEditCell(params.row, params.field)}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'PPL factor',
      field: 'PPLFactor',
      minWidth: 170,
      renderCell(params) {
        if (
          resolveLoadTypeFromLeg(params.row) === 'Checkpoint' ||
          (params.row.load && params.row.unload)
        ) {
          return null;
        } else if (params.row.PPLFactor === '') {
          return (
            <Box width={'100%'}>
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #000',
                  color: '#ffffff',
                }}
              >
                <Typography align="left">
                  <label>{'0'}</label>
                </Typography>
              </div>
            </Box>
          );
        } else if (params.row.load) {
          return (
            <Box width={'100%'}>
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #000',
                }}
              >
                <div
                  style={{
                    width: `${
                      params.row.PPLFactor > 100 ? 100 : params.row.PPLFactor
                    }%`,
                    backgroundColor: `${getBackColor(params.row.PPLFactor)}`,
                    paddingLeft: 4,
                  }}
                >
                  <Typography align="left">
                    <label>{params.row.loadPPLSum}</label>
                    <label>{' | '}</label>
                    <label>
                      <strong>{params.row.PPLFactor}%</strong>
                    </label>
                  </Typography>
                </div>
              </div>
            </Box>
          );
        } else {
          return (
            <Box width={'100%'}>
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #000',
                }}
              >
                <div
                  style={{
                    width: `${
                      params.row.PPLFactor > 100 ? 100 : params.row.PPLFactor
                    }%`,
                    backgroundColor: `${getBackColor(params.row.PPLFactor)}`,
                    paddingLeft: 4,
                  }}
                >
                  <Typography align="left">
                    <label>{params.row.unloadPPLSum}</label>
                    <label>{' | '}</label>
                    <label>
                      <strong>{params.row.PPLFactor}%</strong>
                    </label>
                  </Typography>
                </div>
              </div>
            </Box>
          );
        }
      },
    },
    {
      headerName: 'Cages',
      field: 'cages',
      minWidth: 110,
      editable: true,
      type: 'number',
      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            editable={canEditCell(params.row, params.field)}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Note from terminal',
      field: 'terminalNote',
      minWidth: 100,
      editable: true,
      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Leg note',
      field: 'note',
      minWidth: 100,
    },
    {
      headerName: 'Car Gate Number',
      field: 'carGateNumber',
      minWidth: 100,
      editable: true,
      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            editable={true}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Trailer Gate Number',
      field: 'trailerGateNumber',
      minWidth: 100,
      editable: true,
      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            editable={canEditCell(params.row, params.field)}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Car Registration Number',
      field: 'carRegistrationNumber',
      minWidth: 100,
      editable: false,
      renderCell(params: GridCellParams) {
        return (
          <EditDriverDetailsModal
            routeId={params.row.routeId2}
            legId={params.row.legId}
            routeName={params.row.routeId}
            transportationDate={params.row.transportationDate}
            cellValue={params.value as string}
            tableName={TABLE_NAMES.TruckFillAndTime}
            onRowUpdate={(updatedFields) =>
              onDriverDetailsUpdate?.({
                ...updatedFields,
                id: params.row.legId,
              })
            }
          />
        );
      },
    },
    {
      headerName: 'Trailer Registration Number',
      field: 'trailerRegistrationNumber',
      minWidth: 100,
      editable: false,
      renderCell(params: GridCellParams) {
        return (
          <EditDriverDetailsModal
            routeId={params.row.routeId2}
            legId={params.row.legId}
            routeName={params.row.routeId}
            transportationDate={params.row.transportationDate}
            cellValue={params.value as string}
            tableName={TABLE_NAMES.TruckFillAndTime}
            onRowUpdate={(updatedFields) =>
              onDriverDetailsUpdate?.({
                ...updatedFields,
                id: params.row.legId,
              })
            }
          />
        );
      },
    },
    {
      headerName: ' Seal Number',
      field: 'sealNumber',
      minWidth: 110,
      editable: true,
      renderCell(params) {
        if (
          params.row.load === true ||
          (params.row.unload === true && params.row.isSealBroken === true)
        ) {
          return (
            <EditableCell
              highlightField={true}
              apiRef={apiRef}
              editable={canEditCell(params.row, params.field)}
              className={classes.editableCell}
              {...params}
            />
          );
        } else {
          return null;
        }
      },
    },
    {
      headerName: 'Is Seal Broken',
      field: 'sealBroken',
      align: 'center',
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const { unload, legId, isSealBroken, load } = row;
        if (unload === true && load === false) {
          return (
            <Checkbox
              style={{ padding: 0, color: '#fc3926' }}
              checked={isSealBroken}
              size="small"
              onChange={(_, checked) => handleSealBrokenChecked(legId, checked)}
            />
          );
        } else {
          return null;
        }
      },
      valueGetter: (params: GridValueGetterParams) => {
        const { row } = params;
        const { unload, isSealBroken, load } = row;
        if (unload === true && load === false) {
          return isSealBroken;
        }
      },
    },
    {
      headerName: 'Production Group',
      field: 'productionGroup',
      minWidth: 100,
      editable: true,
      type: 'string',
      valueParser: (value) => {
        return value ? value.toString().replaceAll(' ', '').toUpperCase() : '';
      },
      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Terminal Responsible',
      field: 'terminalResponsible',
      minWidth: 110,
      editable: true,
      type: 'string',
      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      headerName: 'Operator',
      field: 'operator',
      minWidth: 110,
      editable: true,
      type: 'string',
      renderCell(params) {
        return (
          <EditableCell
            highlightField={true}
            apiRef={apiRef}
            className={classes.editableCell}
            {...params}
          />
        );
      },
    },
    {
      field: 'uploadImage',
      headerName: 'Image file',
      minWidth: 180,
      type: 'actions',
      renderCell: (params: GridCellParams) => {
        const imageCount =
          (params.row.files as DocumentFile[] | undefined)?.length ?? 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Tooltip title={`${t('actions.uploadImage')}`}>
              <IconButton
                className={classes.iconStyle}
                aria-label={`${t(
                  'actions.uploadImage',
                )}, ${imageCount} attached`}
                onClick={() => {
                  handleUploadClick({
                    routeName: params.row.routeId,
                    legId: params.row.legId,
                    subcontractorName: params.row.subcontractorName,
                    existingFiles: params.row.files ?? [],
                  });
                }}
              >
                <UploadFileIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" noWrap sx={{ ml: 0.5 }}>
              {imageCount} {imageCount === 1 ? 'image' : 'images'} attached
            </Typography>
          </Box>
        );
      },
    },
    {
      headerName: 'Updated By',
      field: 'updatedBy',
      minWidth: 100,
    },
    {
      headerName: 'Updated at',
      field: 'updatedAt',
      type: 'dateTime',
      minWidth: 100,
      valueGetter({ value }) {
        return new Date(value as string);
      },
      valueFormatter({ value }) {
        return format(value as Date, 'HH:mm:ss - dd-MM-yyyy');
      },
    },
    {
      field: 'trafficType',
      headerName: t('attributes.trafficType'),
      minWidth: 100,
      type: 'singleSelect',
      valueOptions: ['Inbound', 'Outbound', 'Import', 'Export'],
    },
    {
      headerName: t(`attributes.rutekode`),
      field: 'rutekode',
      minWidth: 100,
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
          noTextWrap={noTextWrap}
        />
      ),
    },
    {
      headerName: 'tom',
      field: 'tom',
      minWidth: 100,
      type: 'boolean',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
          noTextWrap={noTextWrap}
        />
      ),
    },
    {
      headerName: 'Togref',
      field: 'togref',
      minWidth: 100,
      type: 'string',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
          noTextWrap={noTextWrap}
        />
      ),
    },
    {
      headerName: t(`attributes.containerId`),
      field: 'containerId',
      minWidth: 100,
      type: 'string',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
          noTextWrap={noTextWrap}
        />
      ),
    },
    {
      headerName: 'Turnummer',
      field: 'turnummer',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
          noTextWrap={noTextWrap}
        />
      ),
    },
    {
      headerName: 'bpx',
      field: 'bpx',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'pri03',
      field: 'pri03',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'pri49',
      field: 'pri49',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'upri03',
      field: 'upri03',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'upri49',
      field: 'upri49',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'parti',
      field: 'parti',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'brev',
      field: 'brev',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'pru',
      field: 'pru',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: 'pip',
      field: 'pip',
      minWidth: 100,
      type: 'number',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: t(`attributes.inneholdsbeskrivelse`),
      field: 'inneholdsbeskrivelse',
      minWidth: 200,
      type: 'string',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
          noTextWrap={noTextWrap}
        />
      ),
    },
    {
      headerName: t(`attributes.adr`),
      field: 'adr',
      minWidth: 100,
      type: 'string',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
        />
      ),
    },
    {
      headerName: t(`attributes.trainNumber`),
      field: 'trainNumber',
      minWidth: 100,
      type: 'string',
      renderCell: (params: GridCellParams) => (
        <EditNorwayColumnsModal
          params={params}
          routeIdOverride={params.row.routeId2}
          legIdOverride={params.row.firstLegId}
          carDetailIdOverride={params.row.firstLegCarDetailId}
          rowKeyOverride={params.row.id}
          initialDataOverrides={{
            togref: params.row.togref,
            containerId: params.row.containerId,
          }}
          onNorwayColumnsUpdate={onNorwayColumnsUpdate}
          noTextWrap={noTextWrap}
        />
      ),
    },
    {
      headerName: t(`attributes.transportType`),
      field: 'transportType',
      minWidth: 100,
      type: 'string',
      valueGetter({ row }) {
        if (row.transportType == null) {
          return '';
        }
        return row.transportType;
      },
    },
    {
      headerName: 'Assigned',
      field: 'assigned',
      minWidth: 100,
      type: 'boolean',
      editable: false,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const { assigned } = row;

        return (
          <Checkbox
            style={{ padding: 0, color: '#ff0202b8' }}
            checked={assigned}
            size="small"
            disabled
          />
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.assigned,
    },
    {
      headerName: 'ansvBefraktare',
      field: 'ansvBefraktare',
      minWidth: 120,
      type: 'string',
    },
    {
      headerName: 'Timestamp when assigned',
      field: 'timeWhenAssigned',
      minWidth: 50,
      width: 120,
      type: 'string',
    },
  ];

  colArr.forEach((col: any) => {
    if (typeof col?.field === 'string') {
      col.getApplyQuickFilterFn = makeVisibilityAwareQuickFilter(col.field);
    }
  });

  if (tftConfig.columnOrder || tftConfig.columnsWidth) {
    const columnsWithSavedWidths = colArr.map((column) => {
      if (tftConfig?.columnsWidth?.[column.field]?.width) {
        return {
          ...column,
          width: tftConfig.columnsWidth[column.field].width,
        };
      }

      return column;
    });
    const reorderedColumns: GridColumns = [];
    const orderedFields = new Set<string>();

    tftConfig?.columnOrder?.forEach((field: string) => {
      const column = columnsWithSavedWidths.find(
        (col: any) => col?.field === field,
      );

      if (column) {
        reorderedColumns.push(column);
        orderedFields.add(field);
      }
    });

    const missingColumns = columnsWithSavedWidths.filter(
      (column: any) => !orderedFields.has(column.field),
    );

    return reorderedColumns.length > 0
      ? [...reorderedColumns, ...missingColumns]
      : columnsWithSavedWidths.filter((col) => col !== undefined);
  }

  return colArr.filter((col) => col !== undefined);
}
